import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Input, Button, Row, Col, Typography, Select, message, DatePicker, Pagination, Spin, Popconfirm, Form, Alert, Modal } from 'antd';
import { SearchOutlined, ArrowLeftOutlined, SaveOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { admissionService } from '../../../../services/admission';
import { getApplicants, getProjects, getCostsCenters, getBlocks, getAreas, getJobPositions, createJobPosition } from '../../../../services/general';
import { overcapacityCheck } from '../../../../services/reservation';
import moment from 'moment';
import evaluationTypes from '../../../../utils/evaluationType.json';
import debounce from 'lodash/debounce';
import uniqBy from 'lodash/uniqBy';
import ProjectModal from '../ProjectModal';
import SimpleCreateModal from '../SimpleCreateModal';
import config from '../../../../config';
import { useMassiveChargeValidator } from '../../hooks/useMassiveChargeValidator';
import { splitLatinoamericano } from './massiveChargeHeuristics';
import { PRESTATIONS_SEARCH_DEBOUNCE_MS, isIncompleteSearchTerm } from '../../../../utils/searchConfig';
const { Text } = Typography;
const { Option } = Select;
const DOCUMENT_TYPES = [
  { value: 'dni', label: 'DNI' },
  { value: 'passport', label: 'Pasaporte' },
  { value: 'ce', label: 'Carnet de Extranjería' },
];

const EditableCell = React.memo(
  ({ value, recordKey, dataIndex, field, onChange, availableProtocols, fetchProtocols, initialOptions = [], duplicateCount = 0, errorType, originalString, companyId }) => {
    const { t } = useTranslation();
    const [val, setVal] = useState(value);
    const [options, setOptions] = useState(initialOptions);
    const [loading, setLoading] = useState(false);
    const [showBulkConfirm, setShowBulkConfirm] = useState(false);
    const [pendingValue, setPendingValue] = useState(null);
    const [pendingOption, setPendingOption] = useState(null);
    const [projectModal, setProjectModal] = useState({ type: 'create', visible: false });
    const [jobPositionModal, setJobPositionModal] = useState(false);
    const [dummyForm] = Form.useForm();
    useEffect(() => {
      setVal(value);
      if (['applicant', 'project', 'costCenter', 'area', 'jobPosition'].includes(field.key) && value && typeof value === 'object') {
        setOptions((prev) => {
          if (!prev.find((o) => o.id === value.id)) {
            return [...prev, value];
          }
          return prev;
        });
      }
    }, [value, field.key]);
    const fetchUsers = useMemo(
      () =>
        debounce(async (search) => {
          if (!search || search.length < 2) return;
          setLoading(true);
          try {
            const response = await getApplicants({ query: search });
            setOptions(response.data || []);
          } catch (error) {
            console.error('Error fetching applicants:', error);
          } finally {
            setLoading(false);
          }
        }, 500),
      [],
    );
    const fetchProjectsQuery = useMemo(
      () =>
        debounce(async (search) => {
          setLoading(true);
          try {
            const response = await getProjects({ query: search });
            setOptions(response.data || response || []);
          } catch (error) {
            console.error('Error fetching projects:', error);
          } finally {
            setLoading(false);
          }
        }, 500),
      [],
    );
    const fetchAreasQuery = useMemo(
      () =>
        debounce(async (search) => {
          setLoading(true);
          try {
            const response = await getAreas({ query: search, company_id: companyId });
            setOptions(response.data || response || []);
          } catch (error) {
            console.error('Error fetching areas:', error);
          } finally {
            setLoading(false);
          }
        }, 500),
      [],
    );
    const fetchJobPositionsQuery = useMemo(
      () =>
        debounce(async (search) => {
          setLoading(true);
          try {
            const response = await getJobPositions({ query: search, company_id: companyId });
            setOptions(response.data || response || []);
          } catch (error) {
            console.error('Error fetching job positions:', error);
          } finally {
            setLoading(false);
          }
        }, 500),
      [],
    );
    const resolveOptionToValue = (opt, pVal) => {
      if (!opt) return pVal;
      if (opt.user) return opt.user;
      if (opt.project) return opt.project;
      if (opt.costCenter) return opt.costCenter;
      if (opt.area) return opt.area;
      if (opt.jobPosition) return opt.jobPosition;
      return pVal;
    };

    const handleSelect = (newVal, option) => {
      if (['applicant', 'project', 'costCenter', 'area', 'jobPosition'].includes(field.key) && duplicateCount > 0) {
        setPendingValue(newVal);
        setPendingOption(option);
        setShowBulkConfirm(true);
      } else {
        setVal(newVal);
        onChange(recordKey, dataIndex, resolveOptionToValue(option, newVal));
      }
    };
    const confirmBulk = () => {
      setVal(pendingValue);
      onChange(recordKey, dataIndex, resolveOptionToValue(pendingOption, pendingValue), { bulk: true });
      setShowBulkConfirm(false);
    };
    const cancelBulk = () => {
      setVal(pendingValue);
      onChange(recordKey, dataIndex, resolveOptionToValue(pendingOption, pendingValue), { bulk: false });
      setShowBulkConfirm(false);
    };

    const renderWithBulkConfirm = (selectComponent) => {
      if (['applicant', 'project', 'costCenter', 'area', 'jobPosition'].includes(field.key)) {
        const fieldName = t(`reservations.massiveCharge.fields.${field.key}`, { defaultValue: field.key }).toLowerCase();
        return (
          <Popconfirm
            title={t('reservations.massiveCharge.step3ApplyAllTitle', { field: fieldName })}
            description={t('reservations.massiveCharge.step3ApplyAllDesc', { count: duplicateCount, field: fieldName })}
            open={showBulkConfirm}
            onConfirm={confirmBulk}
            onCancel={cancelBulk}
            okText={t('reservations.massiveCharge.step3YesAll')}
            cancelText={t('reservations.massiveCharge.step3OnlyThis')}
            icon={<QuestionCircleOutlined className="confirmation-popover-icon" />}
            placement="topRight"
          >
            {selectComponent}
          </Popconfirm>
        );
      }
      return selectComponent;
    };
    if (field.key === 'protocol') {
      const protocolOptions = [...availableProtocols];
      if (Array.isArray(val)) {
        val.forEach((v) => {
          if (typeof v === 'object' && !protocolOptions.find((o) => o.id === v.id)) {
            protocolOptions.push(v);
          }
        });
      }
      return (
        <Select
          mode="multiple"
          className={`protocol-select-cell ${errorType ? `mc-input-${errorType}` : ''}`}
          placeholder={t('reservations.massiveCharge.step3Select')}
          value={Array.isArray(val) ? val.map((v) => (typeof v === 'object' ? v.id : v)) : []}
          onChange={(newIds) => {
            const newServices = [];
            newIds.forEach((id) => {
              const service = protocolOptions.find((p) => p.id === id);
              if (service) {
                const exists = newServices.some((s) => `${s.id}-${s.type}` === `${service.id}-${service.type}`);
                if (!exists) {
                  newServices.push({
                    id: service.id,
                    name: service.name || service.children,
                    type: service.type,
                    sale_amount: service.sale_amount || 0,
                    department_id: service.department_id,
                    currency_id: service.currency_id || null,
                    attached_service_id: service.attached_service_id || null,
                    _originalName: service.name || service.children,
                  });
                }
                if (!service.attached_service_id && service.attached_services?.length) {
                  service.attached_services.forEach((attached) => {
                    const attachedExists = newServices.some((s) => `${s.id}-${s.type}` === `${attached.id}-${attached.type}`);
                    if (!attachedExists) {
                      newServices.push({
                        id: attached.id,
                        name: attached.name || attached.children,
                        type: attached.type,
                        sale_amount: attached.sale_amount || 0,
                        department_id: attached.department_id,
                        currency_id: service.currency_id || null,
                        _originalName: attached.name || attached.children,
                      });
                    }
                  });
                }
              }
            });
            setVal(newServices);
            onChange(recordKey, dataIndex, newServices);
          }}
          showSearch
          onSearch={fetchProtocols}
          filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
        >
          {protocolOptions.map((p) => (
            <Option key={p.id} value={p.id}>
              {p.name || p._originalName}
            </Option>
          ))}
        </Select>
      );
    }
    if (field.key === 'applicant') {
      const displayValue = val && typeof val === 'object' ? val.id : val;
      const selectComponent = (
        <Select
          showSearch
          className={`full-width-select ${errorType ? `mc-input-${errorType}` : ''}`}
          placeholder={t('reservations.massiveCharge.step3SearchApplicant')}
          defaultActiveFirstOption={false}
          showArrow={false}
          filterOption={false}
          onSearch={fetchUsers}
          onChange={handleSelect}
          notFoundContent={loading ? <Spin size="small" /> : null}
          value={displayValue}
          allowClear
          optionLabelProp="label"
        >
          {options.map((user) => (
            <Option key={user.id} value={user.id} label={user.email || user.fullname || user.name} user={user}>
              <div className="massive-charge-applicant-option">
                <Text strong>{user.email || t('reservations.massiveCharge.step3NoEmail', 'Sin correo')}</Text>
                <Text type="secondary" className="massive-charge-applicant-option-desc">{user.fullname || `${user.name} ${user.last_name || ''}`}</Text>
              </div>
            </Option>
          ))}
        </Select>
      );
      return renderWithBulkConfirm(selectComponent);
    }
    if (field.key === 'project') {
      const displayValue = val && typeof val === 'object' ? val.id : val;
      return (
        <div className="project-select-cell-wrapper">
          {renderWithBulkConfirm(
            <Select
              className={`full-width-select ${errorType ? `mc-input-${errorType}` : ''}`}
              placeholder={t('reservations.massiveCharge.step3SelectProject')}
              showSearch
              showArrow={false}
              filterOption={false}
              allowClear
              onSearch={fetchProjectsQuery}
              onChange={handleSelect}
              value={displayValue}
              notFoundContent={
                loading ? (
                  <Spin size="small" />
                ) : (
                  <div className="empty-search project-empty-search">
                    <div className="empty-text">{t('reservations.massiveCharge.step3NoProjects')}</div>
                    <Button type="link" onClick={() => setProjectModal({ type: 'create', visible: true })}>
                      {t('reservations.massiveCharge.step3CreateProject')}
                    </Button>
                  </div>
                )
              }
            >
              {options.map((project) => (
                <Option key={project.id} value={project.id} project={project}>
                  {project.name || project.business_name}
                </Option>
              ))}
            </Select>
          )}
          <ProjectModal
            modal={projectModal}
            setModal={setProjectModal}
            setter={([newProject]) => {
              if (newProject) {
                setOptions((prev) => [...prev.filter((p) => p.id !== newProject.id), newProject]);
              }
            }}
            externalForm={dummyForm}
            formName="project_id"
            createTitle={t('reservations.massiveCharge.step3CreateProjectTitle')}
            editTitle={t('reservations.massiveCharge.step3EditProjectTitle')}
            handleModalResult={(formName, responseId) => {
              const createdProj = options.find((o) => o.id === responseId) || { id: responseId };
              handleSelect(responseId, { project: createdProj });
            }}
          />
        </div>
      );
    }
    if (field.key === 'costCenter') {
      const displayValue = val && typeof val === 'object' ? val.id : val;
      return (
        <div className="project-select-cell-wrapper">
          {renderWithBulkConfirm(
            <Select
              className={`full-width-select ${errorType ? `mc-input-${errorType}` : ''}`}
              placeholder={t('reservations.massiveCharge.step3SelectCostCenter')}
              showSearch
              showArrow={false}
              filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
              allowClear
              onChange={(newVal, option) => handleSelect(newVal, option ? { costCenter: options.find((o) => o.id === option.value) } : null)}
              value={displayValue}
              notFoundContent={
                loading ? (
                  <Spin size="small" />
                ) : (
                  <div className="empty-search project-empty-search">
                    <div className="empty-text">{t('reservations.massiveCharge.step3NoCostCenters')}</div>
                  </div>
                )
              }
            >
              {options.map((cc) => (
                <Option key={cc.id} value={cc.id}>
                  {cc.name || cc.business_name}
                </Option>
              ))}
            </Select>
          )}
        </div>
      );
    }
    if (field.key === 'area') {
      const displayValue = val && typeof val === 'object' ? val.id : val;
      return (
        <div className="project-select-cell-wrapper">
          {renderWithBulkConfirm(
            <Select
              className={`full-width-select ${errorType ? `mc-input-${errorType}` : ''}`}
              placeholder={t('reservations.massiveCharge.step3SelectArea')}
              showSearch
              showArrow={false}
              filterOption={false}
              allowClear
              onSearch={fetchAreasQuery}
              onChange={handleSelect}
              value={displayValue}
              notFoundContent={
                loading ? (
                  <Spin size="small" />
                ) : (
                  <div className="empty-search project-empty-search">
                    <div className="empty-text">{t('reservations.massiveCharge.step3NoAreas')}</div>
                  </div>
                )
              }
            >
              {options.map((area) => (
                <Option key={area.id} value={area.id} area={area}>
                  {area.name}
                </Option>
              ))}
            </Select>
          )}
        </div>
      );
    }
    if (field.key === 'jobPosition') {
      const displayValue = val && typeof val === 'object' ? val.id : val;
      return (
        <div className="project-select-cell-wrapper">
          {renderWithBulkConfirm(
            <Select
              className={`full-width-select ${errorType ? `mc-input-${errorType}` : ''}`}
              placeholder={t('reservations.massiveCharge.step3SelectJobPosition')}
              showSearch
              showArrow={false}
              filterOption={false}
              allowClear
              onSearch={fetchJobPositionsQuery}
              onChange={handleSelect}
              value={displayValue}
              notFoundContent={
                loading ? (
                  <Spin size="small" />
                ) : (
                  <div className="empty-search project-empty-search">
                    <div className="empty-text">{t('reservations.massiveCharge.step3NoJobPositions')}</div>
                    <Button type="link" onClick={() => setJobPositionModal(true)}>
                      {t('reservations.massiveCharge.step3CreateJobPosition', 'Crear puesto')}
                    </Button>
                  </div>
                )
              }
            >
              {options.map((jobPosition) => (
                <Option key={jobPosition.id} value={jobPosition.id} jobPosition={jobPosition}>
                  {jobPosition.name}
                </Option>
              ))}
            </Select>
          )}
          <SimpleCreateModal
            visible={jobPositionModal}
            setVisible={setJobPositionModal}
            title={t('reservations.massiveCharge.step3CreateJobPositionTitle', 'Crear puesto')}
            inputLabel={t('reservations.massiveCharge.step3JobPositionName', 'Nombre del puesto')}
            createFn={(params) => createJobPosition({ ...params, company_id: companyId })}
            initialName={originalString || ''}
            onCreated={(newJobPosition) => {
              setOptions((prev) => [...prev.filter((p) => p.id !== newJobPosition.id), newJobPosition]);
              handleSelect(newJobPosition.id, { jobPosition: newJobPosition });
            }}
          />
        </div>
      );
    }
    if (field.key === 'reservationDate' || field.key === 'birthDate') {
      const mValue = val ? moment(val, ['DD-MM-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true) : null;
      return (
        <DatePicker
          className={`full-width-select ${errorType ? `mc-input-${errorType}` : ''}`}
          format="DD-MM-YYYY"
          value={mValue && mValue.isValid() ? mValue : null}
          disabledDate={field.key === 'reservationDate' ? (current) => current && current < moment().startOf('day') : undefined}
          onChange={(_, dateString) => {
            setVal(dateString);
            onChange(recordKey, dataIndex, dateString);
          }}
          placeholder={t('reservations.massiveCharge.step3SelectDate')}
        />
      );
    }
    if (field.key === 'documentType') {
      return (
        <Select
          className={`full-width-select ${errorType ? `mc-input-${errorType}` : ''}`}
          value={String(val || '').toLowerCase()}
          onChange={(newVal) => {
            setVal(newVal);
            onChange(recordKey, dataIndex, newVal);
          }}
          placeholder={t('reservations.massiveCharge.step3SelectType')}
        >
          {DOCUMENT_TYPES.map((t) => (
            <Option key={t.value} value={t.value}>
              {t.label}
            </Option>
          ))}
        </Select>
      );
    }
    if (field.key === 'evaluationType') {
      const peEvals = evaluationTypes.PE || [];
      const currentVal = peEvals.find((e) => e.text === val || String(e.value) === String(val));
      return (
        <Select
          className={`full-width-select ${errorType ? `mc-input-${errorType}` : ''}`}
          value={currentVal ? currentVal.text : val}
          onChange={(newVal) => {
            setVal(newVal);
            onChange(recordKey, dataIndex, newVal);
          }}
          placeholder={t('reservations.massiveCharge.step3SelectType')}
        >
          {peEvals.map((e) => (
            <Option key={e.value} value={e.text}>
              {e.text}
            </Option>
          ))}
        </Select>
      );
    }
    if (field.key === 'gender') {
      return (
        <Select
          className={`full-width-select ${errorType ? `mc-input-${errorType}` : ''}`}
          value={String(val || '')}
          onChange={(newVal) => {
            setVal(newVal);
            onChange(recordKey, dataIndex, newVal);
          }}
          placeholder={t('reservations.massiveCharge.step3SelectGender')}
          allowClear
        >
          <Option value="Masculino">{t('reservations.massiveCharge.step3Male')}</Option>
          <Option value="Femenino">{t('reservations.massiveCharge.step3Female')}</Option>
        </Select>
      );
    }
    return (
      <Input
        value={val}
        onChange={(e) => {
          setVal(e.target.value);
          onChange(recordKey, dataIndex, e.target.value);
        }}
        className={errorType ? `mc-input-${errorType}` : ''}
      />
    );
  },
);
const addUniqueService = (servicesArray, newService) => {
  const exists = servicesArray.some((s) => `${s.id}-${s.type}` === `${newService.id}-${newService.type}`);
  if (!exists) {
    servicesArray.push({
      id: newService.id,
      name: newService.name || newService.children,
      type: newService.type,
      sale_amount: newService.sale_amount || 0,
      department_id: newService.department_id,
      currency_id: newService.currency_id || null,
      attached_service_id: newService.attached_service_id || null,
      _originalName: newService.name || newService.children,
    });
  }
};
const MassiveChargeStep3 = ({ form, setCurrentStep, fieldsToInclude, mappingData, setFinalDataSource, baseData, setOverCapacityToken }) => {
  const { t } = useTranslation();
  const { validateField } = useMassiveChargeValidator(baseData);
  const [dataSource, setDataSource] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [availableProtocols, setAvailableProtocols] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });
  const [loading, setLoading] = useState(false);
  const [insufficientCapacityDates, setInsufficientCapacityDates] = useState([]);
  const [overcapacityTokenLocal, setOvercapacityTokenLocal] = useState(null);
  const [validatingOvercapacity, setValidatingOvercapacity] = useState(false);
  const [overCapacityForm] = Form.useForm();
  const [checkingCapacity, setCheckingCapacity] = useState(false);
  const capacityCacheRef = React.useRef({});
  const evaluateCapacity = useCallback(
    async (data) => {
      if (!data || data.length === 0 || !baseData?.branch_office_id) return [];
      const dateCounts = {};
      data.forEach((item) => {
        const d = item.reservationDate;
        if (d) {
          const mDate = moment(d, ['DD-MM-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
          if (mDate.isValid()) {
            const formatted = mDate.format('YYYY-MM-DD');
            dateCounts[formatted] = (dateCounts[formatted] || 0) + 1;
          }
        }
      });
      const dates = Object.keys(dateCounts);
      if (dates.length === 0) return [];
      const insufficient = [];
      await Promise.all(
        dates.map(async (date) => {
          let available = capacityCacheRef.current[date];
          if (available === undefined) {
            try {
              const response = await getBlocks({
                branchOffice: baseData.branch_office_id,
                date: date,
                department: 1,
              });
              available = 0;
              if (response && response.blocks) {
                available = response.blocks.reduce((acc, block) => {
                  const occupied = block.occupied || block.reservations_count || block.count || 0;
                  return acc + Math.max(0, (block.max_capacity || 0) - occupied);
                }, 0);
              }
              capacityCacheRef.current[date] = available;
            } catch (e) {
              console.error(`Error fetching blocks for ${date}:`, e);
              available = 0;
            }
          }
          const requested = dateCounts[date];
          if (requested > available) {
            insufficient.push({ date: moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY'), short: requested - available });
          }
        }),
      );
      return insufficient;
    },
    [baseData?.branch_office_id],
  );
  useEffect(() => {
    if (!mappingData || !mappingData.fileData || !mappingData.mapping) return;
    const { fileData, mapping } = mappingData;
    const fetchData = async () => {
      setLoading(true);
      const initialTransformed = fileData.map((row, index) => {
        const record = { key: index.toString() };
        fieldsToInclude.forEach((field) => {
          const mappedColumns = mapping[field.key];
          if (field.key === 'protocol') {
            const protocols = [];
            if (Array.isArray(mappedColumns)) {
              mappedColumns.forEach((col) => {
                const val = row[col];
                if (val) protocols.push(String(val).trim());
              });
            }
            record._rawProtocols = protocols;
          } else if (field.key === 'names') {
            const mappedColName = mapping['names'];
            let val = row[mappedColName];
            if (val && typeof val === 'object' && val.text) val = val.text;
            if (mapping['names'] === mapping['lastnames']) {
              const { names, lastnames } = splitLatinoamericano(val);
              record.names = names;
              record.lastnames = lastnames;
            } else {
              record.names = val == null ? '' : String(val).trim();
            }
          } else if (field.key === 'lastnames') {
            if (mapping['names'] === mapping['lastnames']) return;
            const mappedColName = mapping['lastnames'];
            let val = row[mappedColName];
            if (val && typeof val === 'object' && val.text) val = val.text;
            record.lastnames = val == null ? '' : String(val).trim();
          } else {
            let val = row[mappedColumns];
            if (val && typeof val === 'object' && val.text) val = val.text;
            record[field.key] = val == null ? '' : String(val).trim();
          }
        });
        record.originalApplicantString = record.applicant;
        record.originalProjectString = record.project;
        record.originalCostCenterString = record.costCenter;
        record.originalAreaString = record.area;
        record.originalJobPositionString = record.jobPosition;
        return record;
      });
      const uniqueProtocolNames = [...new Set(initialTransformed.flatMap((r) => r._rawProtocols))];
      const protocolLookupMap = {};
      if (uniqueProtocolNames.length > 0) {
        const protocolResults = await Promise.all(
          uniqueProtocolNames.map(async (name) => {
            try {
              if (!name?.trim()) return;
              const response = await admissionService.getServices({
                query: name,
                limit: 10,
                exclude_exams: true,
                company_id: baseData?.company_id,
                outsourcing_id: baseData?.outsourcing_id,
                third_party_id: baseData?.third_company_id,
                branch_office_id: baseData?.branch_office_id
              });
              const services = response.data || response || [];
              const match =
                services.find((s) => (s.name || s.children || '').toLowerCase() === name.toLowerCase()) || (services.length > 0 ? services[0] : null);
              return { name, match };
            } catch (e) {
              console.error(`Error looking up protocol: ${name}`, e);
              return { name, match: null };
            }
          }),
        );
        protocolResults.forEach((r) => {
          if (r && r?.name) {
            protocolLookupMap[r.name] = r.match;
          }
        });
      }
      const uniqueApplicants = [...new Set(initialTransformed.map((r) => r.originalApplicantString).filter((a) => a && typeof a === 'string'))];
      const applicantLookupMap = {};
      if (uniqueApplicants.length > 0) {
        const applicantResults = await Promise.all(
          uniqueApplicants.map(async (query) => {
            try {
              const response = await getApplicants({ query, limit: 10 });
              const allUsers = response.data || [];
              const foundUser = allUsers.length === 1 ? allUsers[0] : null;
              return { query, foundUser, allUsers };
            } catch (e) {
              console.error(`Error looking up applicant: ${query}`, e);
              return { query, foundUser: null, allUsers: [] };
            }
          }),
        );
        applicantResults.forEach((res) => {
          applicantLookupMap[res.query] = { foundUser: res.foundUser, allUsers: res.allUsers };
        });
      }
      const uniqueProjects = [...new Set(initialTransformed.map((r) => r.originalProjectString).filter((p) => p && typeof p === 'string'))];
      const projectLookupMap = {};
      if (uniqueProjects.length > 0) {
        const projectResults = await Promise.all(
          uniqueProjects.map(async (query) => {
            try {
              const response = await getProjects({ query, limit: 10 });
              const allProjects = response.data || response || [];
              const matchProject =
                allProjects.find((p) => (p.name || '').toLowerCase() === query.trim().toLowerCase()) ||
                (allProjects.length === 1 ? allProjects[0] : null);
              return { query, matchProject, allProjects };
            } catch (e) {
              console.error(`Error looking up project: ${query}`, e);
              return { query, matchProject: null, allProjects: [] };
            }
          }),
        );
        projectResults.forEach((res) => {
          projectLookupMap[res.query] = { matchProject: res.matchProject, allProjects: res.allProjects };
        });
      }
      const uniqueCostCenters = [...new Set(initialTransformed.map((r) => r.originalCostCenterString).filter((c) => c && typeof c === 'string'))];
      const costCenterLookupMap = {};
      let allCompanyCostCentersCache = [];
      const uniqueAreas = [...new Set(initialTransformed.map((r) => r.originalAreaString).filter((a) => a && typeof a === 'string'))];
      const areaLookupMap = {};
      if (uniqueAreas.length > 0) {
        const areaResults = await Promise.all(
          uniqueAreas.map(async (query) => {
            try {
              const response = await getAreas({ query, limit: 10, company_id: baseData?.company_id });
              const allAreas = response.data || response || [];
              const matchArea =
                allAreas.find((a) => (a.name || '').toLowerCase() === query.trim().toLowerCase()) ||
                (allAreas.length === 1 ? allAreas[0] : null);
              return { query, matchArea, allAreas };
            } catch (e) {
              console.error(`Error looking up area: ${query}`, e);
              return { query, matchArea: null, allAreas: [] };
            }
          }),
        );
        areaResults.forEach((res) => {
          areaLookupMap[res.query] = { matchArea: res.matchArea, allAreas: res.allAreas };
        });
      }
      const uniqueJobPositions = [...new Set(initialTransformed.map((r) => r.originalJobPositionString).filter((j) => j && typeof j === 'string'))];
      const jobPositionLookupMap = {};
      if (uniqueJobPositions.length > 0) {
        const jobPositionResults = await Promise.all(
          uniqueJobPositions.map(async (query) => {
            try {
              const response = await getJobPositions({ query, limit: 10, company_id: baseData?.company_id });
              const allJobPositions = response.data || response || [];
              const matchJobPosition =
                allJobPositions.find((j) => (j.name || '').toLowerCase() === query.trim().toLowerCase()) ||
                (allJobPositions.length === 1 ? allJobPositions[0] : null);
              return { query, matchJobPosition, allJobPositions };
            } catch (e) {
              console.error(`Error looking up job position: ${query}`, e);
              return { query, matchJobPosition: null, allJobPositions: [] };
            }
          }),
        );
        jobPositionResults.forEach((res) => {
          jobPositionLookupMap[res.query] = { matchJobPosition: res.matchJobPosition, allJobPositions: res.allJobPositions };
        });
      }
      try {
        const [costCentersResponse] = await Promise.all([
          getCostsCenters({ companyId: baseData?.company_id, limit: 1000 })
        ]);
        allCompanyCostCentersCache = costCentersResponse.data || costCentersResponse || [];
        if (uniqueCostCenters.length > 0) {
          uniqueCostCenters.forEach((query) => {
            const match = allCompanyCostCentersCache.find((c) => (c.name || c.business_name || '').toLowerCase() === query.trim().toLowerCase());
            costCenterLookupMap[query] = { matchCostCenter: match, allCostCenters: allCompanyCostCentersCache };
          });
        }
      } catch (e) {
        console.error(`Error looking up cost centers`, e);
      }
      const finalData = initialTransformed.map((record) => {
        const mappedServices = [];
        record._rawProtocols?.forEach((pName) => {
          const match = protocolLookupMap[pName];
          if (match) {
            addUniqueService(mappedServices, match);
            if (match.attached_services?.length) {
              match.attached_services.forEach((attached) => {
                addUniqueService(mappedServices, {
                  ...attached,
                  currency_id: match.currency_id,
                });
              });
            }
          }
        });
        const appLookup = applicantLookupMap[record.originalApplicantString] || { foundUser: null, allUsers: [] };
        const projLookup = projectLookupMap[record.originalProjectString] || { matchProject: null, allProjects: [] };
        const costCenterLookup = costCenterLookupMap[record.originalCostCenterString] || {
          matchCostCenter: null,
          allCostCenters: allCompanyCostCentersCache,
        };
        const areaLookup = areaLookupMap[record.originalAreaString] || {
          matchArea: null,
          allAreas: [],
        };
        const jobPositionLookup = jobPositionLookupMap[record.originalJobPositionString] || {
          matchJobPosition: null,
          allJobPositions: [],
        };
        return {
          ...record,
          protocol: mappedServices,
          applicant: appLookup.foundUser,
          initialApplicantOptions: appLookup.allUsers,
          project: projLookup.matchProject,
          initialProjectOptions: projLookup.allProjects,
          costCenter: costCenterLookup.matchCostCenter,
          initialCostCenterOptions: costCenterLookup.allCostCenters,
          area: areaLookup.matchArea,
          initialAreaOptions: areaLookup.allAreas,
          jobPosition: jobPositionLookup.matchJobPosition,
          initialJobPositionOptions: jobPositionLookup.allJobPositions,
        };
      });
      setDataSource(finalData);
      const initialInsufficient = await evaluateCapacity(finalData);
      setInsufficientCapacityDates(initialInsufficient);
      setLoading(false);
    };
    fetchData();
  }, [mappingData, fieldsToInclude, form, baseData, evaluateCapacity]);
  const fetchProtocols = useMemo(() => debounce(async (search = '') => {
    if (isIncompleteSearchTerm(search)) return;
    try {
      const response = await admissionService.getServices({
        query: search,
        limit: 50,
        exclude_exams: true,
        company_id: baseData?.company_id,
        outsourcing_id: baseData?.outsourcing_id,
        third_party_id: baseData?.third_company_id,
        branch_office_id: baseData?.branch_office_id
      });
      const results = response.data || response || [];
      setAvailableProtocols(uniqBy(results, 'id'));
    } catch (error) {
      console.error('Error fetching protocols:', error);
    }
  }, PRESTATIONS_SEARCH_DEBOUNCE_MS), [baseData?.company_id, baseData?.outsourcing_id, baseData?.third_company_id, baseData?.branch_office_id]);

  useEffect(() => {
    fetchProtocols();
    return () => {
      fetchProtocols.cancel();
    };
  }, [fetchProtocols]);
  const checkCapacity = useMemo(
    () =>
      debounce(async (data) => {
        setCheckingCapacity(true);
        const insufficient = await evaluateCapacity(data);
        setInsufficientCapacityDates(insufficient);
        setCheckingCapacity(false);
      }, 1000),
    [evaluateCapacity],
  );
  useEffect(() => {
    checkCapacity(dataSource);
  }, [dataSource, checkCapacity]);
  const filteredData = useMemo(() => {
    if (!searchText) return dataSource;
    const lowerSearch = searchText.toLowerCase();
    return dataSource.filter((item) => {
      const fullName = `${item.names} ${item.lastnames}`.toLowerCase();
      const doc = String(item.documentNumber || '').toLowerCase();
      return fullName.includes(lowerSearch) || doc.includes(lowerSearch);
    });
  }, [dataSource, searchText]);
  const handleCellChange = useCallback((key, dataIndex, value, options = {}) => {
    setDataSource((prev) => {
      const index = prev.findIndex((item) => key === item.key);
      if (index === -1) return prev;
      const record = prev[index];
      const isConfigField = ['applicant', 'project', 'costCenter', 'area', 'jobPosition'].includes(dataIndex);
      const newData = [...prev];
      newData[index] = { ...record, [dataIndex]: value };
      if (isConfigField && options.bulk) {
        const originalKey = `original${dataIndex.charAt(0).toUpperCase() + dataIndex.slice(1)}String`;
        const originalName = record[originalKey];
        newData.forEach((item, idx) => {
          if (item[originalKey] === originalName) {
            newData[idx] = { ...item, [dataIndex]: value };
          }
        });
        message.success(t('reservations.massiveCharge.step3FieldUpdated', { defaultValue: 'Registros actualizados masivamente.' }));
      }
      return newData;
    });
  }, [t]);
  const validationErrors = useMemo(() => {
    const errors = {};
    dataSource.forEach((record) => {
      const recordErrors = [];
      fieldsToInclude.forEach((field) => {
        const val = record[field.key];
        if (field.mandatory && (val === null || val === undefined || val === '')) {
          recordErrors.push({ field: field.key, message: t('reservations.massiveCharge.step3MandatoryEmpty'), isMandatory: true });
        } else {
          const validationObj = validateField(field.key, val, record);
          if (validationObj) {
            const isStr = typeof validationObj === 'string';
            const msg = isStr ? validationObj : validationObj.message;
            const type = isStr ? null : validationObj.type;
            recordErrors.push({ field: field.key, message: msg, isMandatory: field.mandatory, type });
          }
        }
      });
      if (recordErrors.length > 0) {
        errors[record.key] = recordErrors;
      }
    });
    return errors;
  }, [dataSource, fieldsToInclude, validateField, t]);
  const columns = useMemo(() => {
    return fieldsToInclude.map((field) => {
      const isMandatory = field.mandatory;
      return {
        title: (
          <span className="massive-charge-table-header-title">
            {isMandatory && <span className="massive-charge-table-header-mandatory">*</span>}
            {t(`reservations.massiveCharge.fields.${field.key}`)}
          </span>
        ),
        dataIndex: field.key,
        key: field.key,
        width: 220,
        render: (text, record) => {
          const isConfigField = ['applicant', 'project', 'costCenter', 'area', 'jobPosition'].includes(field.key);
          const originalKey = `original${field.key.charAt(0).toUpperCase() + field.key.slice(1)}String`;

          const otherMatches = (isConfigField && record[originalKey])
            ? dataSource.filter(
              (item) =>
                item.key !== record.key &&
                item[originalKey] === record[originalKey] &&
                (!item[field.key] || item[field.key].id !== text?.id),
            ).length
            : 0;

          return (
            <EditableCell
              value={text}
              recordKey={record.key}
              dataIndex={field.key}
              field={field}
              onChange={handleCellChange}
              availableProtocols={availableProtocols}
              fetchProtocols={fetchProtocols}
              initialOptions={
                {
                  applicant: record.initialApplicantOptions,
                  project: record.initialProjectOptions,
                  costCenter: record.initialCostCenterOptions,
                  area: record.initialAreaOptions,
                  jobPosition: record.initialJobPositionOptions
                }[field.key] || []
              }
              duplicateCount={otherMatches}
              errorType={
                (() => {
                  const recordErrs = validationErrors[record.key];
                  if (!recordErrs) return null;
                  const fieldErr = recordErrs.find(e => e.field === field.key);
                  if (!fieldErr) return null;
                  return fieldErr.type || (fieldErr.isMandatory ? 'error' : 'warning');
                })()
              }
              originalString={isConfigField ? record[originalKey] : ''}
              companyId={baseData?.company_id}
            />
          );
        },
      };
    });
  }, [fieldsToInclude, t, dataSource, handleCellChange, availableProtocols, fetchProtocols, baseData?.company_id, validationErrors]);
  const handleValidateOvercapacity = async () => {
    try {
      setValidatingOvercapacity(true);
      const values = await overCapacityForm.validateFields();
      const response = await overcapacityCheck(values);
      if (response && response.length) {
        setOvercapacityTokenLocal(values.code);
        message.success(t('reservations.massiveCharge.step3CodeSuccess'));
      } else {
        message.error(t('reservations.massiveCharge.step3CodeInvalid'));
      }
    } catch (err) {
      if (!err.errorFields) {
        message.error(t('reservations.massiveCharge.step3CodeError'));
        console.error('Error al validar código sobrecupo', err);
      }
    } finally {
      setValidatingOvercapacity(false);
    }
  };
  const isCampaignBranch = useMemo(() => {
    if (config?.campaingBranches && baseData?.branch_office_id) {
      const allowedIds = config.campaingBranches.split(',').map(id => String(id).trim());
      return allowedIds.includes(String(baseData.branch_office_id));
    }
    return false;
  }, [baseData?.branch_office_id]);
  const handleFinish = () => {
    let hasBlockingErrors = false;
    Object.values(validationErrors).forEach(errList => {
      if (errList.some(e => (!e.type || e.type === 'error') && e.isMandatory)) {
        hasBlockingErrors = true;
      }
    });
    if (hasBlockingErrors) {
      message.error(t('reservations.massiveCharge.step3BlockingErrors'));
      return;
    }
    if (insufficientCapacityDates.length > 0 && !overcapacityTokenLocal && !isCampaignBranch) {
      message.error(t('reservations.massiveCharge.step3OvercapacityRequired'));
      return;
    }
    if (setOverCapacityToken) {
      setOverCapacityToken(overcapacityTokenLocal);
    }
    setFinalDataSource(dataSource);
    setCurrentStep(3);
  };
  const paginatedData = useMemo(() => {
    const start = (pagination.current - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, pagination]);
  const handleSearch = useCallback((e) => {
    setSearchText(e.target.value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, []);
  const hasBlockingErrors = useMemo(() => {
    return Object.values(validationErrors).some((errList) =>
      errList.some((e) => (!e.type || e.type === 'error') && e.isMandatory),
    );
  }, [validationErrors]);

  const hasUnresolvedCapacity = useMemo(() => {
    return !isCampaignBranch && insufficientCapacityDates.length > 0 && !overcapacityTokenLocal;
  }, [isCampaignBranch, insufficientCapacityDates, overcapacityTokenLocal]);

  return (
    <div className="massive-charge-step3">
      <Row justify="center" className="mb-20">
        <Col span={24}>
          <h3 className="massive-charge-step-title">{t('reservations.massiveCharge.step3ValidationTitle')}</h3>
          <p className="massive-charge-step-description">
            {t('reservations.massiveCharge.step3ValidationDesc')}
          </p>
        </Col>
      </Row>
      <Row justify="space-between" align="middle" className="mb-15 table-header-actions">
        <Col>
          <Text strong className="total-records">
            {t('reservations.massiveCharge.step3TotalRecords', { count: dataSource.length })}
            {searchText && t('reservations.massiveCharge.step3Filtered', { count: filteredData.length })}
          </Text>
        </Col>
        <Col className="search-input-wrapper">
          <Input placeholder={t('reservations.massiveCharge.step3SearchNameOrDoc')} prefix={<SearchOutlined />} onChange={handleSearch} allowClear />
        </Col>
      </Row>
      <Spin spinning={loading || checkingCapacity} tip={t('reservations.massiveCharge.step3Syncing')}>
        <div className="massive-charge-table-container">
          <Table
            dataSource={paginatedData}
            columns={columns}
            pagination={false}
            size="middle"
            bordered
            className="massive-charge-editable-table"
            style={{ minWidth: columns.length * 220 }}
          />
        </div>
      </Spin>
      <Row justify="start" className="mt-15">
        <Col>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={filteredData.length}
            onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
            showSizeChanger={false}
          />
        </Col>
      </Row>
      {Object.keys(validationErrors).length > 0 && (
        <Row className="mt-15">
          <Col span={24}>
            <Alert
              type="error"
              className="overcapacity-alert"
              showIcon
              icon={<span className="icon-error-rounded" title="icon-error-rounded" />}
              message={<strong>{t('reservations.massiveCharge.step3ErrorsDetected')}</strong>}
              description={
                <ul className="massive-charge-errors-list">
                  {Object.entries(validationErrors).map(([recordKey, errList]) => {
                    const record = dataSource.find(r => r.key === recordKey);
                    const identifier = record ? `${record.names || ''} ${record.lastnames || ''}`.trim() || t('reservations.massiveCharge.step3Document', { document: record.documentNumber }) || t('reservations.massiveCharge.step3UnidentifiedRow') : recordKey;
                    const blockingErrors = errList.filter(e => (!e.type || e.type === 'error') && e.isMandatory).map(e => `${t(`reservations.massiveCharge.fields.${e.field}`)}: ${e.message}`);
                    const nonBlockingErrors = errList.filter(e => (!e.type || e.type === 'warning') && !e.isMandatory).map(e => `${t(`reservations.massiveCharge.fields.${e.field}`)}: ${e.message}`);
                    const suggestionErrors = errList.filter(e => e.type === 'suggestion').map(e => `${t(`reservations.massiveCharge.fields.${e.field}`)}: ${e.message}`);
                    return (
                      <li key={recordKey}>
                        <strong>{identifier}</strong>:
                        {blockingErrors.length > 0 && <span className="massive-charge-error-text">{t('reservations.massiveCharge.step3BlockingPrefix')} {blockingErrors.join(', ')}.</span>}
                        {nonBlockingErrors.length > 0 && <span className="massive-charge-warning-text">{t('reservations.massiveCharge.step3WarningPrefix')} {nonBlockingErrors.join(', ')}.</span>}
                        {suggestionErrors.length > 0 && <span className="massive-charge-suggestion-text">[Sugerencia] {suggestionErrors.join(', ')}.</span>}
                      </li>
                    );
                  })}
                </ul>
              }
            />
          </Col>
        </Row>
      )}
      {insufficientCapacityDates.length > 0 && (
        <Row className="mt-20">
          <Col span={24}>
            {isCampaignBranch ? (
              <Alert
                type="info"
                className="campaign-info-alert"
                showIcon
                icon={<span className="icon-info-rounded" />}
                message={t('reservations.massiveCharge.step3OvercapacityEnabled')}
                description={t('reservations.massiveCharge.step3CampaignInfo')}
              />
            ) : (
              <Alert
                type="error"
                className="overcapacity-alert"
                showIcon
                icon={<span className="icon-alert-triangle-bold" />}
                message={t('reservations.massiveCharge.step3NoEnoughCapacity')}
                description={
                  <div>
                    <ul className="mb-10">
                      {insufficientCapacityDates.map((item, idx) => (
                        <li key={idx}>
                          <strong>{item.date}</strong>: {t('reservations.massiveCharge.step3MissingShort', { count: item.short })}
                        </li>
                      ))}
                    </ul>
                    {!overcapacityTokenLocal ? (
                      <div className="mt-10">
                        <p className="massive-charge-auth-text">{t('reservations.massiveCharge.step3AuthKey')}</p>
                        <Form form={overCapacityForm} layout="inline" onFinish={handleValidateOvercapacity}>
                          <Form.Item name="code" rules={[{ required: true, message: t('reservations.massiveCharge.step3EnterCode') }]}>
                            <Input placeholder={t('reservations.massiveCharge.step3OvercapacityCode')} />
                          </Form.Item>
                          <Form.Item>
                            <Button type="primary" htmlType="submit" loading={validatingOvercapacity}>
                              {t('reservations.massiveCharge.step3AuthorizeOvercapacity')}
                            </Button>
                          </Form.Item>
                        </Form>
                      </div>
                    ) : (
                      <div className="mt-10 mc-text-success">
                        <strong>{t('reservations.massiveCharge.step3CodeValidated')}</strong>
                      </div>
                    )}
                  </div>
                }
              />
            )}
          </Col>
        </Row>
      )}
      <Row justify="space-between" className="mt-30">
        <Col>
          <Button className="cmt-button primary outline" size="large" icon={<ArrowLeftOutlined />} onClick={() => setCurrentStep(1)}>
            {t('reservations.massiveCharge.step3BackToMapping')}
          </Button>
        </Col>
        <Col>
          <Button
            type="primary"
            className={`cmt-button primary ${hasBlockingErrors || hasUnresolvedCapacity || checkingCapacity ? 'disabled' : ''}`}
            size="large"
            icon={<SaveOutlined />}
            onClick={handleFinish}
            disabled={hasBlockingErrors || hasUnresolvedCapacity || checkingCapacity}
          >
            {t('reservations.massiveCharge.step3LoadReservations')}
          </Button>
        </Col>
      </Row>
    </div>
  );
};
export default MassiveChargeStep3;
